import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  Menu, LogOut, User as UserIcon, Users, LayoutDashboard, X,
  Wallet, ChevronDown, QrCode, Landmark, ShoppingBag, Award, Trophy, TrendingUp, Crown, HandCoins,
  Network, ShieldCheck, ArrowLeftRight, Building2, Gift, Coins, CalendarDays, HeartPulse, Ambulance, BookOpen, Lock,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

export default function Header() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const inFundSection = location.pathname.startsWith("/fund-deposit");
  const inTransferSection = location.pathname.startsWith("/fund-transfer");
  const inTeamSection = location.pathname.startsWith("/team") || location.pathname === "/referrals";
  const inIncomeSection = ["/direct-income", "/rewards", "/level-income", "/rank-income", "/daily-referral-bonus"].includes(location.pathname);
  const inInsuranceSection = location.pathname.startsWith("/insurance");
  const inProfileSection = location.pathname.startsWith("/profile");
  const [fundOpen, setFundOpen] = useState(inFundSection);
  const [transferOpen, setTransferOpen] = useState(inTransferSection);
  const [teamOpen, setTeamOpen] = useState(inTeamSection);
  const [incomeOpen, setIncomeOpen] = useState(inIncomeSection);
  const [insuranceOpen, setInsuranceOpen] = useState(inInsuranceSection);
  const [profileOpen, setProfileOpen] = useState(inProfileSection);

  useEffect(() => {
    if (inFundSection) setFundOpen(true);
  }, [inFundSection]);

  useEffect(() => {
    if (inTransferSection) setTransferOpen(true);
  }, [inTransferSection]);

  useEffect(() => {
    if (inTeamSection) setTeamOpen(true);
  }, [inTeamSection]);

  useEffect(() => {
    if (inIncomeSection) setIncomeOpen(true);
  }, [inIncomeSection]);

  useEffect(() => {
    if (inInsuranceSection) setInsuranceOpen(true);
  }, [inInsuranceSection]);

  useEffect(() => {
    if (inProfileSection) setProfileOpen(true);
  }, [inProfileSection]);

  const initials = (() => {
    const parts = (user?.name || "U").trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return parts[0].slice(0, 2).toUpperCase();
  })();

  const doLogout = () => {
    logout();
    navigate("/login");
  };

  const isActive = (p) => location.pathname === p;

  const navItemCls = (active) =>
    `flex items-center gap-3 px-5 py-3 text-sm transition-colors ${
      active
        ? "bg-red-50 text-red-700 font-semibold border-l-4 border-red-700"
        : "text-slate-700 hover:bg-slate-50 border-l-4 border-transparent"
    }`;

  const subItemCls = (active) =>
    `flex items-center gap-3 pl-14 pr-5 py-2.5 text-sm transition-colors ${
      active
        ? "text-red-700 font-semibold bg-red-50/60"
        : "text-slate-600 hover:bg-slate-50"
    }`;

  return (
    <>
      <header
        data-testid="app-header"
        className="sticky top-0 z-40 text-white shadow-lg"
        style={{ backgroundColor: "#5b0f0f" }}
      >
        <div className="max-w-6xl mx-auto grid grid-cols-[auto_1fr_auto] items-center h-20 px-4 gap-3">
          <button
            data-testid="menu-toggle-btn"
            aria-label="Open menu"
            onClick={() => setOpen(true)}
            className="h-10 w-10 rounded-md bg-white/10 hover:bg-white/15 flex items-center justify-center active:scale-95 transition-transform"
          >
            <Menu size={22} />
          </button>
          <Link to="/dashboard" className="flex flex-col items-center leading-tight text-center">
            <div className="flex items-center gap-3">
              <img
                src="https://customer-assets-jt897jd0.emergentagent.net/job_ref-connect-app/artifacts/02ztij1f_logo.png"
                alt="Unique Deal India"
                data-testid="brand-logo"
                className="h-12 w-12 rounded-md bg-white p-0.5 shadow-sm object-contain"
              />
              <div
                className="text-white text-2xl sm:text-3xl font-extrabold tracking-wide whitespace-nowrap"
                style={{ fontFamily: "Georgia, 'Times New Roman', serif", letterSpacing: "0.02em" }}
              >
                UNIQUE DEAL INDIA
              </div>
            </div>
            <div className="text-[11px] sm:text-xs text-white/85 mt-0.5">
              Earn While You Learn
            </div>
          </Link>
          <button
            data-testid="header-avatar"
            onClick={() => setOpen(true)}
            className="h-10 w-10 rounded-lg bg-purple-500 text-white font-bold flex items-center justify-center border border-white/20 hover:bg-purple-600 transition-colors"
          >
            {initials}
          </button>
        </div>
      </header>

      {open && (
        <div
          data-testid="side-menu-overlay"
          className="fixed inset-0 z-50 bg-black/50"
          onClick={() => setOpen(false)}
        >
          <aside
            onClick={(e) => e.stopPropagation()}
            className="absolute left-0 top-0 h-full w-72 bg-white shadow-2xl flex flex-col"
          >
            <div className="p-5 bg-red-700 text-white flex items-center justify-between" style={{ backgroundColor: "#5b0f0f" }}>
              <div className="flex items-center gap-3">
                <img
                  src="https://customer-assets-jt897jd0.emergentagent.net/job_ref-connect-app/artifacts/02ztij1f_logo.png"
                  alt=""
                  className="h-9 w-9 rounded-md bg-white p-0.5 object-contain"
                />
                <div>
                  <div className="font-bold">{user?.name}</div>
                  <div className="text-xs opacity-80">{user?.user_code}</div>
                </div>
              </div>
              <button
                data-testid="close-menu-btn"
                onClick={() => setOpen(false)}
                className="p-1 rounded hover:bg-white/10"
              >
                <X size={20} />
              </button>
            </div>
            <nav className="flex-1 py-2 overflow-y-auto">
              <Link
                to="/dashboard"
                data-testid="nav-dashboard"
                onClick={() => setOpen(false)}
                className={navItemCls(isActive("/dashboard"))}
              >
                <LayoutDashboard size={18} className="text-red-700" /> Dashboard
              </Link>

              {user?.activated ? (
                <Link
                  to="/ebooks"
                  data-testid="nav-ebooks"
                  onClick={() => setOpen(false)}
                  className={navItemCls(isActive("/ebooks"))}
                >
                  <BookOpen size={18} className="text-red-700" /> E-Book Gallery
                </Link>
              ) : (
                <button
                  type="button"
                  data-testid="nav-ebooks-locked"
                  onClick={() => {
                    toast.error("Activate your account by purchasing a product to unlock the E-Book Gallery.");
                  }}
                  className={`${navItemCls(false)} w-full justify-between text-slate-400 cursor-not-allowed`}
                >
                  <span className="flex items-center gap-3">
                    <BookOpen size={18} className="text-slate-400" /> E-Book Gallery
                  </span>
                  <Lock size={14} className="text-slate-400" />
                </button>
              )}

              <button
                type="button"
                data-testid="nav-fund-deposit-toggle"
                onClick={() => setFundOpen((v) => !v)}
                className={`${navItemCls(inFundSection)} w-full justify-between`}
              >
                <span className="flex items-center gap-3">
                  <Wallet size={18} className="text-red-700" /> Fund Deposit
                </span>
                <ChevronDown
                  size={16}
                  className={`transition-transform text-slate-500 ${fundOpen ? "rotate-180" : ""}`}
                />
              </button>
              <div
                className={`overflow-hidden transition-all duration-300 ${
                  fundOpen ? "max-h-40 opacity-100" : "max-h-0 opacity-0 pointer-events-none"
                }`}
                aria-hidden={!fundOpen}
              >
                <Link
                  to="/fund-deposit/qr"
                  data-testid="nav-fund-qr"
                  onClick={() => setOpen(false)}
                  className={subItemCls(isActive("/fund-deposit/qr"))}
                >
                  <QrCode size={14} /> QR-Code
                </Link>
                <Link
                  to="/fund-deposit/bank"
                  data-testid="nav-fund-bank"
                  onClick={() => setOpen(false)}
                  className={subItemCls(isActive("/fund-deposit/bank"))}
                >
                  <Landmark size={14} /> Bank Deposit
                </Link>
              </div>

              <Link
                to="/products"
                data-testid="nav-products"
                onClick={() => setOpen(false)}
                className={navItemCls(isActive("/products"))}
              >
                <ShoppingBag size={18} className="text-red-700" /> Product
              </Link>

              <button
                type="button"
                data-testid="nav-fund-transfer-toggle"
                onClick={() => setTransferOpen((v) => !v)}
                className={`${navItemCls(inTransferSection)} w-full justify-between`}
              >
                <span className="flex items-center gap-3">
                  <ArrowLeftRight size={18} className="text-red-700" /> Fund Transfer
                </span>
                <ChevronDown size={16}
                  className={`transition-transform text-slate-500 ${transferOpen ? "rotate-180" : ""}`} />
              </button>
              <div
                className={`overflow-hidden transition-all duration-300 ${
                  transferOpen ? "max-h-60 opacity-100" : "max-h-0 opacity-0 pointer-events-none"
                }`}
                aria-hidden={!transferOpen}
              >
                <Link to="/fund-transfer#main" data-testid="nav-transfer-ewallet"
                      onClick={() => setOpen(false)}
                      className={subItemCls(isActive("/fund-transfer"))}>
                  <Wallet size={14} /> Transfer to E-Wallet
                </Link>
                <Link to="/fund-transfer#bank" data-testid="nav-transfer-bank"
                      onClick={() => setOpen(false)}
                      className={subItemCls(false)}>
                  <Building2 size={14} /> Bank Withdrawal
                </Link>
                <Link to="/fund-transfer#user" data-testid="nav-transfer-user"
                      onClick={() => setOpen(false)}
                      className={subItemCls(false)}>
                  <Users size={14} /> Transfer to user E-Wallet
                </Link>
              </div>

              <Link
                to="/rank-status"
                data-testid="nav-rank-status"
                onClick={() => setOpen(false)}
                className={navItemCls(isActive("/rank-status"))}
              >
                <Award size={18} className="text-red-700" /> Rank Status
              </Link>

              <button
                type="button"
                data-testid="nav-team-details-toggle"
                onClick={() => setTeamOpen((v) => !v)}
                className={`${navItemCls(inTeamSection)} w-full justify-between`}
              >
                <span className="flex items-center gap-3">
                  <Network size={18} className="text-red-700" /> Team Details
                </span>
                <ChevronDown
                  size={16}
                  className={`transition-transform text-slate-500 ${teamOpen ? "rotate-180" : ""}`}
                />
              </button>
              <div
                className={`overflow-hidden transition-all duration-300 ${
                  teamOpen ? "max-h-40 opacity-100" : "max-h-0 opacity-0 pointer-events-none"
                }`}
                aria-hidden={!teamOpen}
              >
                <Link
                  to="/team/level"
                  data-testid="nav-team-level"
                  onClick={() => setOpen(false)}
                  className={subItemCls(isActive("/team/level"))}
                >
                  <Users size={14} /> Level Team
                </Link>
                <Link
                  to="/referrals"
                  data-testid="nav-referrals"
                  onClick={() => setOpen(false)}
                  className={subItemCls(isActive("/referrals"))}
                >
                  <Users size={14} /> My Referrals
                </Link>
                <Link
                  to="/team/genealogy"
                  data-testid="nav-team-genealogy"
                  onClick={() => setOpen(false)}
                  className={subItemCls(isActive("/team/genealogy"))}
                >
                  <Users size={14} /> Genealogy
                </Link>
              </div>

              <button
                type="button"
                data-testid="nav-income-toggle"
                onClick={() => setIncomeOpen((v) => !v)}
                className={`${navItemCls(inIncomeSection)} w-full justify-between`}
              >
                <span className="flex items-center gap-3">
                  <Coins size={18} className="text-red-700" /> Income
                </span>
                <ChevronDown
                  size={16}
                  className={`transition-transform text-slate-500 ${incomeOpen ? "rotate-180" : ""}`}
                />
              </button>
              <div
                className={`overflow-hidden transition-all duration-300 ${
                  incomeOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0 pointer-events-none"
                }`}
                aria-hidden={!incomeOpen}
              >
                <Link
                  to="/direct-income"
                  data-testid="nav-direct-income"
                  onClick={() => setOpen(false)}
                  className={subItemCls(isActive("/direct-income"))}
                >
                  <HandCoins size={14} /> Direct Income
                </Link>
                <Link
                  to="/level-income"
                  data-testid="nav-level-income"
                  onClick={() => setOpen(false)}
                  className={subItemCls(isActive("/level-income"))}
                >
                  <TrendingUp size={14} /> Level Income
                </Link>
                <Link
                  to="/daily-referral-bonus"
                  data-testid="nav-daily-referral-bonus"
                  onClick={() => setOpen(false)}
                  className={subItemCls(isActive("/daily-referral-bonus"))}
                >
                  <CalendarDays size={14} /> Daily Referral Bonus
                </Link>
                <Link
                  to="/rewards"
                  data-testid="nav-rewards"
                  onClick={() => setOpen(false)}
                  className={subItemCls(isActive("/rewards"))}
                >
                  <Trophy size={14} /> Level Upgrade Rewards
                </Link>
                <Link
                  to="/rank-income"
                  data-testid="nav-rank-income"
                  onClick={() => setOpen(false)}
                  className={subItemCls(isActive("/rank-income"))}
                >
                  <Crown size={14} /> Rank Income
                </Link>
              </div>

              <Link
                to="/reward-clubs"
                data-testid="nav-reward-clubs"
                onClick={() => setOpen(false)}
                className={navItemCls(isActive("/reward-clubs"))}
              >
                <Gift size={18} className="text-red-700" /> Rewards
              </Link>

              <button
                type="button"
                data-testid="nav-insurance-toggle"
                onClick={() => setInsuranceOpen((v) => !v)}
                className={`${navItemCls(inInsuranceSection)} w-full justify-between`}
              >
                <span className="flex items-center gap-3">
                  <ShieldCheck size={18} className="text-red-700" /> Insurance
                </span>
                <ChevronDown
                  size={16}
                  className={`transition-transform text-slate-500 ${insuranceOpen ? "rotate-180" : ""}`}
                />
              </button>
              <div
                className={`overflow-hidden transition-all duration-300 ${
                  insuranceOpen ? "max-h-40 opacity-100" : "max-h-0 opacity-0 pointer-events-none"
                }`}
                aria-hidden={!insuranceOpen}
              >
                <Link
                  to="/insurance/medical"
                  data-testid="nav-medical-insurance"
                  onClick={() => setOpen(false)}
                  className={subItemCls(isActive("/insurance/medical"))}
                >
                  <HeartPulse size={14} /> Medical Insurance
                </Link>
                <Link
                  to="/insurance/accidental"
                  data-testid="nav-accidental-insurance"
                  onClick={() => setOpen(false)}
                  className={subItemCls(isActive("/insurance/accidental"))}
                >
                  <Ambulance size={14} /> Accidental Insurance
                </Link>
              </div>

              <button
                type="button"
                data-testid="nav-profile-toggle"
                onClick={() => setProfileOpen((v) => !v)}
                className={`${navItemCls(inProfileSection)} w-full justify-between`}
              >
                <span className="flex items-center gap-3">
                  <UserIcon size={18} className="text-red-700" /> Profile
                </span>
                <ChevronDown
                  size={16}
                  className={`transition-transform text-slate-500 ${profileOpen ? "rotate-180" : ""}`}
                />
              </button>
              <div
                className={`overflow-hidden transition-all duration-300 ${
                  profileOpen ? "max-h-40 opacity-100" : "max-h-0 opacity-0 pointer-events-none"
                }`}
                aria-hidden={!profileOpen}
              >
                <Link
                  to="/profile"
                  data-testid="nav-profile"
                  onClick={() => setOpen(false)}
                  className={subItemCls(isActive("/profile"))}
                >
                  <UserIcon size={14} /> My Profile
                </Link>
                <Link
                  to="/profile/kyc"
                  data-testid="nav-profile-kyc"
                  onClick={() => setOpen(false)}
                  className={subItemCls(isActive("/profile/kyc"))}
                >
                  <ShieldCheck size={14} /> KYC
                </Link>
              </div>
            </nav>
            <div className="p-4 border-t">
              <Button
                data-testid="logout-btn"
                variant="outline"
                className="w-full border-red-600 text-red-700 hover:bg-red-50"
                onClick={doLogout}
              >
                <LogOut size={16} className="mr-2" /> Sign out
              </Button>
            </div>
          </aside>
        </div>
      )}
    </>
  );
}
